import { Injectable, Inject, Optional, InjectionToken } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

export const Wiki_BASE_URL = new InjectionToken<string>('Wiki_BASE_URL');

/**
 * Deployed path the app is served under (environment.base_href, e.g. '/ims/').
 * Supplied by the app because the shared tree cannot read a per-project environment,
 * and because `<base href>` is only rewritten to the real value by the CI build flag.
 */
export const Wiki_ROUTE_PREFIX = new InjectionToken<string>('Wiki_ROUTE_PREFIX');

/** Wiki subtree the related-articles lookup is scoped to. */
export const WIKI_ARTICLES_PREFIX = 'articles/Omni/Articles';

/** Public documentation site the panel links out to. */
export const WIKI_DOCS_BASE_URL = 'https://docs.yallaomni.com/docs';

export interface RelatedArticleViewModel {
  path: string;
  url: string;
  title: string;
  description: string;
  score: number;
}

export interface RelatedArticlesViewModel {
  route: string;
  count: number;
  results: RelatedArticleViewModel[];
}

/**
 * Looks up the help articles related to a portal route.
 *
 * The wiki host sits outside the API gateway, so these calls are made with a plain
 * HttpClient and are deliberately left undecorated by the auth interceptor
 * (`secureRoutes` only covers environment.apiGatewayBaseURL).
 *
 * The base URL arrives through {@link Wiki_BASE_URL} rather than an environment
 * import: this file lives in the shared `src/` tree that every project compiles,
 * so it cannot reach a per-project environment file. Apps that do not provide the
 * token leave the service unconfigured and `isConfigured` stays false.
 */
@Injectable({
  providedIn: 'root',
})
export class WikiHelpService {
  private http: HttpClient;
  private baseUrl: string;
  private routePrefix: string;

  constructor(
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(Wiki_BASE_URL) baseUrl?: string,
    @Optional() @Inject(Wiki_ROUTE_PREFIX) routePrefix?: string,
  ) {
    this.http = http;
    this.baseUrl = baseUrl ?? '';
    this.routePrefix = routePrefix ?? '';
  }

  /** False when the hosting app has not wired {@link Wiki_BASE_URL}. */
  get isConfigured(): boolean {
    return this.baseUrl.length > 0;
  }

  /**
   * Turns a router url into the deployed path the wiki indexes articles against,
   * e.g. '/IMSKPI?tab=2' becomes '/ims/IMSKPI'.
   */
  buildRoute(routerUrl: string): string {
    const path = routerUrl.split('?')[0].split('#')[0];
    const prefix = this.routePrefix.replace(/\/+$/, '');
    const suffix = path.replace(/^\/+/, '').replace(/\/+$/, '');

    return suffix.length > 0 ? `${prefix}/${suffix}` : `${prefix}/`;
  }

  getRelated(route: string, lang: string, limit: number = 5): Observable<RelatedArticlesViewModel> {
    const params = new HttpParams()
      .set('route', route)
      .set('prefix', WIKI_ARTICLES_PREFIX)
      .set('lang', lang)
      .set('limit', limit);

    return this.http.get<RelatedArticlesViewModel>(this.baseUrl + '/related', { params });
  }

  /** Public docs-site link for a related-article result. */
  getArticleLink(article: RelatedArticleViewModel, lang: string): string {
    return `${WIKI_DOCS_BASE_URL}/${article.url}?lang=${lang}`;
  }
}
